import type React from "react";
import {type MouseEvent, useEffect, useMemo, useRef, useState} from "react";
import {observer} from "mobx-react-lite";
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Skeleton,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import DashboardIcon from '~/icons/svg/DashboardIcon.svg?react';
import DeleteIcon from '~/icons/svg/DeleteIcon.svg?react';
import EditIcon from '~/icons/svg/EditIcon.svg?react';
import SeemoreIcon from '~/icons/svg/SeeMoreIcon.svg?react';
import {useQueryClient} from "@tanstack/react-query";
import moment from "moment";
import {generatePath, useNavigate} from "react-router";
import {routes} from "../../routing/routes";
import {useStores} from "../../stores";
import {notificationStore} from "../../stores/init";
import {useChatContext} from "./context/ChatContext";
import {supersetDashboardsQueryKey, useSupersetDashboards} from "./hooks/useSupersetDashboards";
import {useDeleteDashboardMutation} from "./hooks/useDeleteDashboardMutation";
import {useRenameDashboardMutation} from "./hooks/useRenameDashboardMutation";
import type {UserDashboardRecord} from "./types/chatTypes";
import {DASHGEN_APP_NAME} from "./utils/agentDatabag";

const DashboardCard: React.FC<{
    dashboard: UserDashboardRecord;
    onOpen: () => void;
    onDelete: () => void;
    onRenamed: () => void;
}> = ({dashboard, onOpen, onDelete, onRenamed}) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [isRenaming, setIsRenaming] = useState(false);
    const [localTitle, setLocalTitle] = useState<string | null>(null);
    const [renameValue, setRenameValue] = useState("");
    const renameInputRef = useRef<HTMLInputElement>(null);
    const {mutate: renameDashboard, isPending: isRenamingPending} = useRenameDashboardMutation();
    const menuOpen = Boolean(anchorEl);
    const title = localTitle ?? (dashboard.title || "Untitled dashboard");

    useEffect(() => {
        if (!isRenaming) return;
        const timeout = setTimeout(() => {
            renameInputRef.current?.focus();
            renameInputRef.current?.select();
        }, 100);
        return () => clearTimeout(timeout);
    }, [isRenaming]);

    const handleMenuOpen = (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleRenameClick = () => {
        handleMenuClose();
        setRenameValue(title);
        setIsRenaming(true);
    };

    const handleRenameSubmit = () => {
        if (isRenamingPending) return;
        const next = renameValue.trim();
        if (!next || next === title) {
            setIsRenaming(false);
            setRenameValue(title);
            return;
        }
        renameDashboard(
            {dashboardId: dashboard.dashboard_id, title: next},
            {
                onSuccess: () => {
                    setLocalTitle(next);
                    setIsRenaming(false);
                    onRenamed();
                },
                onError: () => {
                    setIsRenaming(false);
                    setRenameValue(title);
                    notificationStore.show("Could not rename dashboard. Please try again.", {
                        severity: "error",
                        id: `dashboard-rename-error-${dashboard.dashboard_id}`,
                        autoHideDuration: 3000,
                    });
                },
            },
        );
    };

    const handleRenameCancel = () => {
        setIsRenaming(false);
        setRenameValue(title);
    };

    const handleDeleteClick = () => {
        handleMenuClose();
        onDelete();
    };

    return (
        <Box
            onClick={onOpen}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
                if (isRenaming) return;
                if (e.key === "Enter" || e.key === " ") onOpen();
            }}
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                gap: "3px",
                cursor: "pointer",
                textAlign: "left",
                "&:hover .dashboard-card-thumb": {
                    borderColor: "text.disabled",
                    boxShadow: "0 4px 14px -6px rgba(0,0,0,0.18)",
                },
            }}
        >
            <Box
                className="dashboard-card-thumb"
                sx={{
                    position: "relative",
                    aspectRatio: "4 / 3",
                    mb: "7px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: 1,
                    borderRadius: "8px",
                    bgcolor: "action.hover",
                    color: "#C4C6D0",
                    backgroundColor: '#F3F3FA',
                    transition: "box-shadow 0.15s, border-color 0.15s",
                }}
            >
                <DashboardIcon width={48} height={48}/>
            </Box>
            <Typography sx={{fontSize: 10, color: "#ABABAF", fontWeight: 500}}>
                {moment(dashboard.created_at).format("D MMM YYYY")}
            </Typography>
            <Stack direction="row" alignItems="center" gap={0.5} sx={{minWidth: 0}}>
                {isRenaming ? (
                    <TextField
                        inputRef={renameInputRef}
                        size="small"
                        fullWidth
                        value={renameValue}
                        disabled={isRenamingPending}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onKeyDown={(e) => {
                            e.stopPropagation();
                            if (e.key === "Enter") handleRenameSubmit();
                            if (e.key === "Escape") handleRenameCancel();
                        }}
                        onBlur={handleRenameSubmit}
                        slotProps={{
                            input: {
                                sx: {fontSize: 14, py: 0},
                            },
                        }}
                        sx={{
                            p: 0,
                            flex: 1,
                            "& input": {
                                p: 0,
                            },
                            "& .MuiOutlinedInput-root": {
                                p: 0,
                                "& fieldset": {border: "none"},
                                "&.Mui-focused fieldset": {border: "none"},
                            },
                            "& .MuiOutlinedInput-input": {outline: "none"},
                        }}
                    />
                ) : (
                    <Typography variant="subtitle2" noWrap sx={{flex: 1, minWidth: 0}}>
                        {title}
                    </Typography>
                )}
                {isRenaming ? null : <IconButton
                    size="small"
                    onClick={handleMenuOpen}
                    sx={{flexShrink: 0, p: 0.5}}
                >
                    <SeemoreIcon style={{transform: "rotate(90deg)", width: 18, height: 18}}/>
                </IconButton>}
                <Menu
                    anchorEl={anchorEl}
                    open={menuOpen}
                    onClose={handleMenuClose}
                    disableRestoreFocus
                    onClick={(e) => e.stopPropagation()}
                >
                    <MenuItem onClick={handleRenameClick} sx={{width: '194px'}}>
                        <ListItemIcon>
                            <EditIcon width={18} height={18}/>
                        </ListItemIcon>
                        <ListItemText>Rename</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={handleDeleteClick}>
                        <ListItemIcon>
                            <DeleteIcon color="#BA1A1A" width={18} height={18}/>
                        </ListItemIcon>
                        <ListItemText>Delete</ListItemText>
                    </MenuItem>
                </Menu>
            </Stack>
        </Box>
    );
};

const GRID_SX = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(158px, 1fr))",
    gap: "22px 20px",
    pt: "6px",
} as const;

function dashboardsLoadErrorMessage(error: unknown): string {
    return error instanceof Error
        ? error.message
        : "Failed to load dashboards. Please try again.";
}

export const ChatDashboardsPage: React.FC = observer(() => {
    const navigate = useNavigate();
    const {bagStore} = useStores();
    const {setSelectedAppName, removeSession, setIsSidebarCollapsed} = useChatContext();
    const [query, setQuery] = useState("");
    const {data: dashboards = [], isLoading, isError, error: dashboardsError} = useSupersetDashboards(bagStore.bagId);
    const queryClient = useQueryClient();
    const {mutate: deleteDashboard, isPending: isDeleting} = useDeleteDashboardMutation();
    const [dashboardToDelete, setDashboardToDelete] = useState<UserDashboardRecord | null>(null);

    useEffect(() => {
        setIsSidebarCollapsed(true);
    }, [setIsSidebarCollapsed]);

    const handleConfirmDelete = () => {
        if (!dashboardToDelete) return;
        const {dashboard_id, session_id} = dashboardToDelete;
        deleteDashboard(
            {dashboardId: dashboard_id},
            {
                onSuccess: () => {
                    if (session_id) {
                        removeSession(session_id);
                    }
                    notificationStore.show("Dashboard deleted successfully.", {
                        severity: "success",
                        id: `dashboard-delete-success-${dashboard_id}`,
                        autoHideDuration: 3000,
                    });
                },
                onSettled: () => {
                    setDashboardToDelete(null);
                    void queryClient.invalidateQueries({queryKey: supersetDashboardsQueryKey(bagStore.bagId)});
                },
                onError: () => {
                    notificationStore.show("Could not delete dashboard. Please try again.", {
                        severity: "error",
                        id: `dashboard-delete-error-${dashboard_id}`,
                        autoHideDuration: 3000,
                    });
                },
            },
        );
    };

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return dashboards;
        return dashboards.filter(d => (d.title ?? "").toLowerCase().includes(q));
    }, [dashboards, query]);

    const chatPath = () => generatePath(routes.chat, {id: String(bagStore.bagId)});

    const handleNewDashboard = () => {
        // Maps the design's describe-to-create flow onto the chat: a fresh
        // conversation with the dashboard agent preselected.
        setSelectedAppName(DASHGEN_APP_NAME);
        navigate(chatPath());
    };

    const handleOpen = (dashboard: UserDashboardRecord) => {
        // Open the originating chat session with the embedded dashboard in
        // fullscreen (the panel restores from history; ChatPage applies the flag).
        navigate(
            {pathname: chatPath(), search: `?session_id=${encodeURIComponent(dashboard.session_id)}`},
            {state: {openDashboardFullscreen: true, dashboardUuid: dashboard.embedded_uuid}},
        );
    };

    return (
        <>
            <Box sx={{flexGrow: 1, overflowY: "auto", bgcolor: "background.default"}}>
                <Stack sx={{maxWidth: 880, width: "100%", mx: "auto", px: 5, pt: 4.5, pb: 5, gap: "18px", minHeight: "100%"}}>
                    {isLoading ? (
                        <>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
                                <Skeleton variant="rounded" width={160} height={32}/>
                                <Skeleton variant="rounded" width={148} height={36}/>
                            </Stack>
                            <Skeleton variant="rounded" height={40}/>
                            <Box sx={GRID_SX}>
                                {Array.from({length: 6}, (_, i) => (
                                    <Stack direction="column" key={i} gap={1}>
                                        <Skeleton variant="rounded" sx={{aspectRatio: "4 / 3", height: "auto", mb: "7px", borderRadius: "10px"}}/>
                                        <Skeleton width="40%" height={12}/>
                                        <Skeleton width="70%" height={16}/>
                                    </Stack>
                                ))}
                            </Box>
                        </>
                    ) : isError ? (
                        <>
                            <Typography component="h1" sx={{fontSize: 22, fontWeight: 600, letterSpacing: "-0.02em"}}>
                                Dashboards
                            </Typography>
                            <Alert severity="error">
                                {dashboardsLoadErrorMessage(dashboardsError)}
                            </Alert>
                        </>
                    ) : dashboards.length === 0 ? (
                        <>
                            <Typography component="h1" sx={{fontSize: 22, fontWeight: 600, letterSpacing: "-0.02em"}}>
                                Dashboards
                            </Typography>
                            <Stack alignItems="center" justifyContent="center" sx={{flexGrow: 1, gap: "24px", py: "80px"}}>
                                <DashboardIcon color="#C4C6D0" width={128} height={128} />
                                <Typography>
                                    Create your first dashboard
                                </Typography>
                                <Button variant="outlined" onClick={handleNewDashboard}>
                                    New dashboard
                                </Button>
                            </Stack>
                        </>
                    ) : (
                        <>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
                                <Typography component="h1" sx={{fontSize: 22, fontWeight: 600, letterSpacing: "-0.02em"}}>
                                    Dashboards
                                </Typography>
                                <Button variant="outlined" onClick={handleNewDashboard}>
                                    New dashboard
                                </Button>
                            </Stack>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Search dashboards…"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon fontSize="small"/>
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                            />
                            {filtered.length === 0 ? (
                                <Stack alignItems="center" justifyContent="center" sx={{flexGrow: 1, gap: "18px", py: "80px"}}>
                                    <DashboardIcon color="#C4C6D0" width={128} height={128}/>
                                    <Typography sx={{fontSize: 14, color: "text.secondary"}}>
                                        No dashboards match your search
                                    </Typography>
                                </Stack>
                            ) : (
                                <Box sx={GRID_SX}>
                                    {filtered.map((dashboard) => (
                                        <DashboardCard
                                            key={dashboard.dashboard_id}
                                            dashboard={dashboard}
                                            onOpen={() => handleOpen(dashboard)}
                                            onDelete={() => setDashboardToDelete(dashboard)}
                                            onRenamed={() => {
                                                void queryClient.invalidateQueries({
                                                    queryKey: supersetDashboardsQueryKey(bagStore.bagId),
                                                });
                                            }}
                                        />
                                    ))}
                                </Box>
                            )}
                        </>
                    )}
                </Stack>
            </Box>
            <Dialog
                open={dashboardToDelete !== null}
                onClose={() => !isDeleting && setDashboardToDelete(null)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Delete dashboard?</DialogTitle>
                <DialogContent>
                    <Typography>
                        Dashboard will be permanently deleted, and can't be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDashboardToDelete(null)} disabled={isDeleting}>
                        Cancel
                    </Button>
                    <Button color="error" variant="outlined" onClick={handleConfirmDelete} disabled={isDeleting}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
});
